import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

// Сегментный переключатель: одна дорожка, выбранный сегмент залит сплошным цветом.
//
// Цвет ОДИН на все состояния - фирменный синий. Раскрашивать каждый сегмент своим цветом
// мы пробовали: на таблице из четырнадцати строк получалась радуга, и глаз цеплялся за цвет
// вместо содержания. Смысл несёт подпись, цвет - только то, что выбрано.
//
// Заливка не перерисовывается на месте, а переезжает: измеряем позицию и ширину выбранной
// кнопки и двигаем один общий «камешек» через transform. Лёгкий перелёт в конце
// (cubic-bezier с выходом за единицу) даёт ощущение переката, а не мгновенной подмены.
// Пользователь видит, ОТКУДА и КУДА переключился, - это и есть смысл анимации.
// prefers-reduced-motion гасится глобально в токенах.

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Подсказка при наведении: что произойдёт, если выбрать. */
  title?: string;
  disabled?: boolean;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  disabled,
  size = 'md',
  className,
  'aria-label': ariaLabel,
}: {
  options: Array<SegmentedOption<T>>;
  /** null - ни один сегмент не активен (например, «правило не задано»). */
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
}): ReactNode {
  const trackRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  // Позицию считаем после раскладки: ширина сегмента зависит от длины подписи и шрифта,
  // а шрифт (Onest) приезжает асинхронно - без пересчёта «камешек» встанет мимо.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const active = value == null ? null : itemRefs.current.get(value);
    if (!track || !active) {
      setThumb(null);
      return;
    }
    const measure = () => {
      const t = track.getBoundingClientRect();
      const a = active.getBoundingClientRect();
      setThumb({ left: a.left - t.left, width: a.width });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    ro.observe(active);
    void document.fonts?.ready.then(measure);
    return () => ro.disconnect();
  }, [value, options, size]);

  return (
    <div
      ref={trackRef}
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex rounded-lg border border-line-control bg-panel p-0.5',
        disabled && 'opacity-60',
        className,
      )}
    >
      {thumb ? (
        <span
          aria-hidden
          className="absolute top-0.5 bottom-0.5 left-0 rounded-md bg-accent shadow-blue"
          style={{
            transform: `translateX(${thumb.left}px)`,
            width: thumb.width,
            transition:
              'transform 320ms cubic-bezier(.34,1.32,.64,1), width 320ms cubic-bezier(.34,1.32,.64,1)',
          }}
        />
      ) : null}

      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            ref={(el) => {
              if (el) itemRefs.current.set(o.value, el);
              else itemRefs.current.delete(o.value);
            }}
            type="button"
            role="radio"
            aria-checked={on}
            title={o.title}
            disabled={disabled || o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              'relative z-1 rounded-md font-semibold whitespace-nowrap transition-colors duration-200',
              size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-3.5 py-2 text-[13px]',
              on ? 'text-white' : 'text-muted hover:text-text',
              'disabled:cursor-not-allowed disabled:opacity-45',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
